import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import styles from "./MisPostulacionesLaborales.module.scss";
import { fetchMyJobPostulations } from "../../../../store/actions";
import ProfileEmptyState from "../ProfileEmptyState/ProfileEmptyState.tsx";
import RegisteredUserProfileLoader from "../../../RegisteredUsers/RegisteredUsersSkeleton/RegisteredUserProfileLoader";
import type { MyJobPostulationItem } from "../../../../types/jobRequest.ts";

interface MisPostulacionesLaboralesProps {
  myJobPostulations: MyJobPostulationItem[];
  myJobPostulationsLoading: boolean;
  myJobPostulationsError: string | null;
  fetchMyJobPostulations: () => void;
  setInfoReady: (ready: boolean) => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const MisPostulacionesLaborales: React.FC<MisPostulacionesLaboralesProps> = ({
  myJobPostulations,
  myJobPostulationsLoading,
  myJobPostulationsError,
  fetchMyJobPostulations,
  setInfoReady,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    fetchMyJobPostulations();
  }, [fetchMyJobPostulations]);

  useEffect(() => {
    if (!myJobPostulationsLoading && myJobPostulations !== undefined) {
      setInfoReady(true);
    }
  }, [myJobPostulationsLoading, myJobPostulations, setInfoReady]);

  const sortedPostulations = useMemo(
    () =>
      [...(myJobPostulations || [])].sort(
        (a, b) =>
          new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime()
      ),
    [myJobPostulations]
  );

  if (myJobPostulationsLoading) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={styles.container}
      >
        <motion.div className={styles.loadingContainer}>
          <RegisteredUserProfileLoader />
        </motion.div>
      </motion.section>
    );
  }

  if (myJobPostulationsError) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={styles.container}
      >
        <p className={styles.error} role="alert">
          {myJobPostulationsError}
        </p>
      </motion.section>
    );
  }

  if (!sortedPostulations.length) {
    return (
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={styles.container}
      >
        <ProfileEmptyState
          illustration="generic"
          title="No te postulaste a vacantes laborales"
          description="Cuando te postules a una búsqueda del mercado de trabajo, el historial va a aparecer acá."
          primaryAction={{
            label: "Explorar vacantes",
            onClick: () => navigate("/mercado-de-trabajo/explorar"),
          }}
        />
      </motion.section>
    );
  }

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={styles.container}
    >
      <div className={styles.list}>
        {sortedPostulations.map((postulation) => {
          const jobRequest = postulation.jobRequest;
          const offerId = jobRequest?.id;
          return (
            <button
              key={postulation.id}
              type="button"
              className={styles.card}
              onClick={() => {
                if (offerId) navigate(`/bolsadetrabajo/${offerId}`);
              }}
            >
              <motion.div className={styles.cardTop}>
                <p className={styles.title}>
                  {jobRequest?.description || `Vacante #${offerId ?? postulation.id}`}
                </p>
                <span className={styles.badge}>Postulado</span>
              </motion.div>
              <div className={styles.meta}>
                <span>Postulaste el {formatDate(postulation.appliedAt)}</span>
                {jobRequest?.activeUntil ? (
                  <span>Vigencia hasta {formatDate(jobRequest.activeUntil)}</span>
                ) : null}
              </div>
              {postulation.coverLetter ? (
                <p className={styles.coverPreview}>{postulation.coverLetter}</p>
              ) : null}
            </button>
          );
        })}
      </div>
    </motion.section>
  );
};

const mapStateToProps = (state: any) => ({
  myJobPostulations: state.profile.myJobPostulations,
  myJobPostulationsLoading: state.profile.myJobPostulationsLoading,
  myJobPostulationsError: state.profile.myJobPostulationsError,
});

export default connect(mapStateToProps, { fetchMyJobPostulations })(
  MisPostulacionesLaborales
);
